/**
 * Contextual shimmer skeletons for the Studio (/studio) hub.
 * Pure layout placeholders — no user-facing copy (i18n ratchet).
 */
import React from 'react';
import { Skeleton } from '../../components/ui/Skeleton';

/** One dense RowItem-shaped row (icon + title/desc + pills + actions). */
export function StudioRowSkeleton(): React.ReactElement {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5" aria-hidden>
      <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-baseline gap-2">
          <Skeleton className="h-4 w-36 max-w-[45%]" />
          <Skeleton className="hidden h-3 w-24 sm:block" />
        </div>
        <Skeleton className="h-3 w-3/4 max-w-md" />
      </div>
      <div className="hidden items-center gap-1.5 lg:flex">
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Skeleton className="h-8 w-14 rounded-md" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}

/** Filter chip row placeholder (Templates / Flows / Campaigns). */
export function StudioFilterChipsSkeleton({ count = 6 }: { count?: number }): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-hidden" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-16 shrink-0 rounded-full sm:w-20" />
      ))}
    </div>
  );
}

/** Search field placeholder matching `.topbar-search`. */
export function StudioSearchSkeleton(): React.ReactElement {
  return <Skeleton className="h-10 w-full max-w-md rounded-lg" aria-hidden />;
}

/** Recommended-for-business band on the Templates tab. */
export function TemplatesRecommendedSkeleton(): React.ReactElement {
  return (
    <section className="rounded-xl border border-orange-100 bg-orange-50/40 p-4 sm:p-5" aria-hidden>
      <div className="flex items-start gap-3">
        <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-4 w-48 max-w-full" />
          <Skeleton className="h-3 w-64 max-w-full" />
        </div>
        <Skeleton className="mt-1 hidden h-3 w-20 sm:block" />
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3.5 py-3"
          >
            <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-28 max-w-full" />
              <Skeleton className="h-3 w-36 max-w-full" />
            </div>
            <Skeleton className="h-8 w-12 shrink-0 rounded-md" />
          </div>
        ))}
      </div>
    </section>
  );
}

/** Full Templates gallery shimmer (business-kind resolution + Suspense). */
export function TemplatesGallerySkeleton(): React.ReactElement {
  return (
    <div className="w-full space-y-5 p-4 sm:p-6" role="status" aria-busy="true">
      <TemplatesRecommendedSkeleton />
      <Skeleton className="h-4 w-full max-w-xl" />
      <StudioFilterChipsSkeleton />
      <StudioSearchSkeleton />
      <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
        {Array.from({ length: 8 }).map((_, i) => (
          <StudioRowSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * My Automations (Flows) list body shimmer — first load only.
 * Filter chips + search stay mounted above (already-loaded chrome).
 */
export function FlowsListSkeleton(): React.ReactElement {
  return (
    <div
      className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white"
      role="status"
      aria-busy="true"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <StudioRowSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Campaigns table body shimmer. Filter chips + search stay mounted above.
 */
export function CampaignsListSkeleton(): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white" role="status" aria-busy="true">
      <div className="hidden gap-4 border-b border-gray-100 px-4 py-3 sm:flex" aria-hidden>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      <div className="divide-y divide-gray-100">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3.5" aria-hidden>
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-40 max-w-[60%]" />
              <Skeleton className="h-3 w-24 sm:hidden" />
            </div>
            <Skeleton className="hidden h-5 w-14 rounded-full sm:block" />
            <Skeleton className="hidden h-3 w-12 md:block" />
            <Skeleton className="hidden h-3 w-12 md:block" />
            <div className="flex shrink-0 gap-1.5">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Broadcasts list / form area shimmer. */
export function BroadcastsListSkeleton(): React.ReactElement {
  return (
    <div className="space-y-3" role="status" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between"
          aria-hidden
        >
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-36 max-w-[50%]" />
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
            <Skeleton className="h-3 w-48 max-w-full" />
          </div>
          <Skeleton className="h-8 w-20 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** Starter packs gallery shimmer. */
export function PacksListSkeleton(): React.ReactElement {
  return (
    <div className="w-full space-y-5 p-4 sm:p-6" role="status" aria-busy="true">
      <Skeleton className="h-4 w-full max-w-xl" />
      <StudioFilterChipsSkeleton count={5} />
      <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
        {Array.from({ length: 5 }).map((_, i) => (
          <StudioRowSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/** Chat flows subsection shimmer inside My Automations. */
export function ChatFlowsSectionSkeleton(): React.ReactElement {
  return (
    <div className="space-y-2" role="status" aria-busy="true">
      <div className="flex items-center gap-2 px-1">
        <Skeleton className="h-3.5 w-3.5 rounded" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-6" />
      </div>
      <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3" aria-hidden>
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-32 max-w-[55%]" />
              <Skeleton className="h-3 w-40 max-w-full" />
            </div>
            <Skeleton className="h-5 w-12 rounded-full" />
            <Skeleton className="h-7 w-16 rounded" />
            <Skeleton className="h-7 w-7 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Agents roster grid shimmer (header chrome stays mounted above). */
export function AgentsTabSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" role="status" aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" aria-hidden>
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

/** Recipe picker modal body shimmer. */
export function RecipePickerSkeleton({ count = 3 }: { count?: number }): React.ReactElement {
  return (
    <div className="space-y-2" role="status" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3.5" aria-hidden>
          <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-4 w-36 max-w-[70%]" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
          <Skeleton className="h-7 w-16 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** Lazy-tab Suspense fallback — keeps hub chrome, skeletons the body. */
export function StudioViewSkeleton(): React.ReactElement {
  return <TemplatesGallerySkeleton />;
}
